import {
  Box,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import type { AlertColor } from "@mui/material/Alert/Alert";
import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import useApi from "../../api/apiConfig";
import { practiceSelectionKey } from "../../utils/utils";
import { fetchStudioDocumentBlob } from "./api";
import EntityPreview from "./EntityPreview";
import { buildEntitySchema } from "./entitySchema";
import {
  useCreateStudioSession,
  useMaterializeStudioSession,
  useRunStudioExtraction,
  useSaveStudioTemplate,
  useStudioTemplateDetails,
  useUpdateStudioMapping,
} from "./hooks";
import MappingPanel from "./MappingPanel";
import MaterializeResultView from "./MaterializeResultView";
import {
  bindingKey,
  buildPreviewTree,
  detectIterationRules,
  findMissingRequiredFields,
  flattenJsonPaths,
  formatPreviewValue,
  getJsonValueAtPath,
  suggestPathForField,
} from "./mappingUtils";
import SaveTemplateDialog from "./SaveTemplateDialog";
import StudioExtractTab from "./StudioExtractTab";
import StudioSessionToolbar from "./StudioSessionToolbar";
import StudioStartPanel from "./StudioStartPanel";

const DEFAULT_PROMPT = `Extract all structured data from this PDF (EOB/EOP/ERA/claim report). Output JSON with snake_case keys.
Shape: {"payer": {...}, "payment": {...}, "claims": [{...}]}. Each claim has patient, provider, top-level fields, and a procedures[] array.
Omit missing fields. Money as numbers, dates as YYYY-MM-DD.`;

interface StudioBuilderProps {
  session: StudioSessionMessage | null;
  setSession: (session: StudioSessionMessage | null) => void;
  isExtracting: boolean;
  setIsExtracting: (isExtracting: boolean) => void;
  templates: StudioTemplateMessage[];
  fieldDefinitions: StudioFieldDefinitionsMessage;
  selectedPractices: PracticeMessage[];
  supportedSources: SourceMessage[];
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
}

function extractApiError(error: unknown, fallback: string): string {
  if (
    error instanceof AxiosError &&
    typeof error.response?.data?.detail === "string"
  ) {
    return error.response.data.detail;
  }
  return fallback;
}

export default function StudioBuilder({
  session,
  setSession,
  isExtracting,
  setIsExtracting,
  templates,
  fieldDefinitions,
  selectedPractices,
  supportedSources,
  updateSnackBar,
}: StudioBuilderProps) {
  const selectedPracticesKey = practiceSelectionKey(selectedPractices);
  const [practiceId, setPracticeId] = useState<string>(
    selectedPractices[0]?.wieldyId ?? "",
  );
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState<string>(DEFAULT_PROMPT);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [tab, setTab] = useState<"extract" | "map" | "preview">("extract");
  const [bindings, setBindings] = useState<Record<string, string>>({});
  const [sourceName, setSourceName] = useState<string>("");
  const [saveDialogMode, setSaveDialogMode] = useState<
    "create" | "variant" | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [materializing, setMaterializing] = useState(false);
  const [materializeResult, setMaterializeResult] =
    useState<StudioMaterializeResult | null>(null);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [sampleBlobUrl, setSampleBlobUrl] = useState<string | null>(null);
  const [pendingBlobUrl, setPendingBlobUrl] = useState<string | null>(null);
  const { getClient } = useApi();
  const createSession = useCreateStudioSession();
  const runExtraction = useRunStudioExtraction();
  const updateMapping = useUpdateStudioMapping();
  const materializeSession = useMaterializeStudioSession();
  const saveTemplate = useSaveStudioTemplate();
  const templateDetailsQuery = useStudioTemplateDetails(
    session?.templateId ?? null,
  );
  const templateDetails = templateDetailsQuery.data ?? null;
  const navigate = useNavigate();
  const { search } = window.location;
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode") === "parse" ? "parse" : "train";
  const appliedTemplateMode = !!session?.templateId;

  const entitySchema = useMemo(
    () => buildEntitySchema(fieldDefinitions),
    [fieldDefinitions],
  );

  const flattenedPaths = useMemo(
    () =>
      session?.draftExtraction ? flattenJsonPaths(session.draftExtraction) : [],
    [session?.draftExtraction],
  );

  const iterationRules = useMemo(
    () =>
      session?.draftExtraction
        ? detectIterationRules(session.draftExtraction)
        : [],
    [session?.draftExtraction],
  );

  const pathPreviews = useMemo(() => {
    if (!session?.draftExtraction) return {};
    const map: Record<string, string> = {};
    flattenedPaths.forEach((p) => {
      const v = getJsonValueAtPath(session.draftExtraction, p);
      map[p] = formatPreviewValue(v);
    });
    return map;
  }, [session?.draftExtraction, flattenedPaths]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding session or dropping session?.id would change when this runs
  useEffect(() => {
    setBindings({});
    setSourceName("");
    setTab("extract");
    if (!session) {
      setSelectedTemplateId("");
    }
  }, [session?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding updateSnackBar would re-fire the snackbar
  useEffect(() => {
    if (templateDetailsQuery.isError) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(
          templateDetailsQuery.error,
          "Could not load template details.",
        ),
      });
    }
  }, [templateDetailsQuery.isError]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: session?.id re-applies cached details after the reset effect clears the bindings
  useEffect(() => {
    if (!templateDetails) return;
    const next: Record<string, string> = {};
    templateDetails.bindings.forEach((b) => {
      next[bindingKey(b.entity, b.field)] = b.jsonPath;
    });
    setBindings(next);
    setSourceName(templateDetails.sourceName || "");
  }, [templateDetails, session?.id]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping session?.id would change when this runs
  useEffect(() => {
    if (!session?.draftExtraction || flattenedPaths.length === 0) return;
    if (session.templateId) return;
    if (entitySchema.length === 0) return;
    setBindings((current) => {
      if (Object.keys(current).length > 0) return current;
      const next: Record<string, string> = {};
      entitySchema.forEach((entity) => {
        entity.fields.forEach((field) => {
          const suggestion = suggestPathForField(
            entity.name,
            field.name,
            flattenedPaths,
            iterationRules,
          );
          if (suggestion) {
            next[bindingKey(entity.name, field.name)] = suggestion;
          }
        });
      });
      return next;
    });
  }, [
    session?.id,
    session?.templateId,
    session?.draftExtraction,
    flattenedPaths,
    iterationRules,
    entitySchema,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding selectedPractices[0].wieldyId, selectedPractices[0]?.wieldyId or dropping selectedPracticesKey would change when this runs
  useEffect(() => {
    if (!practiceId && selectedPractices[0]?.wieldyId) {
      setPracticeId(selectedPractices[0].wieldyId);
    }
  }, [selectedPracticesKey, practiceId]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding session or dropping session?.promptText would change when this runs
  useEffect(() => {
    if (session?.promptText) {
      setPrompt(session.promptText);
    } else if (!session) {
      setPrompt(DEFAULT_PROMPT);
    }
  }, [session?.id, session?.promptText]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dropping session?.id would change when this runs
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    const docPath = session?.documentPath;

    setPdfBlobUrl(null);

    if (!docPath) {
      setIsLoadingPdf(false);
      return undefined;
    }

    setIsLoadingPdf(true);
    (async () => {
      try {
        const blob = await fetchStudioDocumentBlob(await getClient(), docPath);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(createdUrl);
      } catch {
        if (!cancelled) {
          updateSnackBar({
            severity: "error",
            message: "Could not load PDF preview.",
          });
        }
      } finally {
        if (!cancelled) setIsLoadingPdf(false);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [session?.id, session?.documentPath, getClient, updateSnackBar]);

  const uploadAndExtract = async (
    file: File,
    practice: string,
    promptForRun: string,
  ) => {
    setIsUploading(true);
    setIsExtracting(true);
    try {
      const created = await createSession.mutateAsync({
        practiceId: practice,
        file,
      });
      setSession(created);
      setIsUploading(false);
      const extracted = await runExtraction.mutateAsync({
        sessionId: created.id,
        promptText: promptForRun,
      });
      setSession(extracted);
      if (extracted.extractionError) {
        updateSnackBar({
          severity: "error",
          message: `Extraction failed: ${extracted.extractionError}`,
        });
      } else {
        updateSnackBar({ severity: "success", message: "Extraction ready." });
      }
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(error, "Upload or extraction failed."),
      });
    } finally {
      setIsUploading(false);
      setIsExtracting(false);
    }
  };

  const handleAnalyze = async () => {
    if (!pendingFile || !practiceId) return;
    setIsUploading(true);
    setIsExtracting(true);
    try {
      const created = await createSession.mutateAsync({
        practiceId,
        file: pendingFile,
        templateId: selectedTemplateId || null,
      });
      setSession(created);
      setIsUploading(false);
      if (created.templateId) {
        updateSnackBar({
          severity: "success",
          message: "Template applied. Reviewing auto-parse…",
        });
      } else if (!created.draftExtraction) {
        const extracted = await runExtraction.mutateAsync({
          sessionId: created.id,
          promptText: prompt,
        });
        setSession(extracted);
        if (extracted.extractionError) {
          updateSnackBar({
            severity: "error",
            message: `Extraction failed: ${extracted.extractionError}`,
          });
        } else {
          updateSnackBar({ severity: "success", message: "Extraction ready." });
        }
      }
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(error, "Upload or extraction failed."),
      });
    } finally {
      setIsUploading(false);
      setIsExtracting(false);
    }
    setPendingFile(null);
  };

  useEffect(() => {
    if (!pendingFile) {
      setPendingBlobUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(pendingFile);
    setPendingBlobUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [pendingFile]);

  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setSampleBlobUrl(null);
    if (!selectedTemplateId) return undefined;
    const tpl = templates.find((t) => t.id === selectedTemplateId);
    if (!tpl?.sampleDocumentPath) return undefined;
    (async () => {
      try {
        const blob = await fetchStudioDocumentBlob(
          await getClient(),
          tpl.sampleDocumentPath as string,
        );
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setSampleBlobUrl(createdUrl);
      } catch {
        if (!cancelled) {
          updateSnackBar({
            severity: "error",
            message: "Could not load sample document.",
          });
        }
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [selectedTemplateId, templates, getClient, updateSnackBar]);

  const handleReplace = async (file: File) => {
    if (!practiceId) return;
    setPendingFile(null);
    setSession(null);
    setIsExtracting(false);
    await uploadAndExtract(file, practiceId, DEFAULT_PROMPT);
    setPrompt(DEFAULT_PROMPT);
  };

  const handleReextract = async () => {
    if (!session) return;
    setIsExtracting(true);
    try {
      const updated = await runExtraction.mutateAsync({
        sessionId: session.id,
        promptText: prompt,
      });
      setSession(updated);
      if (updated.extractionError) {
        updateSnackBar({
          severity: "error",
          message: `Extraction failed: ${updated.extractionError}`,
        });
      } else {
        updateSnackBar({ severity: "success", message: "Re-extracted." });
      }
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(error, "Re-extraction failed."),
      });
    } finally {
      setIsExtracting(false);
    }
  };

  const handleStartOver = () => {
    setPendingFile(null);
    setPrompt(DEFAULT_PROMPT);
    setBindings({});
    setSourceName("");
    setTab("extract");
    setSession(null);
    setIsExtracting(false);
  };

  const handleBindingChange = (
    entity: string,
    field: string,
    path: string | null,
  ) => {
    setBindings((prev) => {
      const key = bindingKey(entity, field);
      const next = { ...prev };
      if (path) {
        next[key] = path;
      } else {
        delete next[key];
      }
      return next;
    });
  };

  const bindingCount = Object.keys(bindings).length;
  const missingRequired = useMemo(
    () => findMissingRequiredFields(bindings, entitySchema),
    [bindings, entitySchema],
  );
  const previewTree = useMemo(
    () =>
      session?.draftExtraction
        ? buildPreviewTree(
            session.draftExtraction,
            bindings,
            iterationRules,
            entitySchema,
          )
        : { payment: [], claims: [] },
    [session?.draftExtraction, bindings, iterationRules, entitySchema],
  );
  const saveBlocked =
    !session?.draftExtraction ||
    bindingCount === 0 ||
    !sourceName ||
    missingRequired.length > 0;

  const handleMaterialize = async () => {
    if (!session) return;
    setMaterializing(true);
    try {
      const bindingsList: StudioBinding[] = Object.entries(bindings).map(
        ([key, jsonPath]) => {
          const [entity, field] = key.split("::");
          return { jsonPath, entity, field };
        },
      );
      await updateMapping.mutateAsync({
        sessionId: session.id,
        draftMapping: {
          bindings: bindingsList,
          sourceName: sourceName || null,
        },
      });
      const r = await materializeSession.mutateAsync(session.id);
      setMaterializeResult(r);
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(error, "Materialize failed."),
      });
    } finally {
      setMaterializing(false);
    }
  };

  const handleRetryMaterialize = () => {
    setMaterializeResult(null);
    setTab("map");
  };

  const handleResultBackToTemplates = () => {
    setMaterializeResult(null);
    handleStartOver();
    navigate(`/studio${search}`);
  };

  const handleResultViewDocument = () => {
    if (pdfBlobUrl) {
      window.open(pdfBlobUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleSaveTemplate = async (input: {
    name: string;
    documentLevelType: string;
    notes: string;
    alsoCreateEntities: boolean;
  }) => {
    if (!session || !sourceName) return;
    setSaving(true);
    try {
      const bindingsList: StudioBinding[] = Object.entries(bindings).map(
        ([key, jsonPath]) => {
          const [entity, field] = key.split("::");
          return { jsonPath, entity, field };
        },
      );
      await saveTemplate.mutateAsync({
        sessionId: session.id,
        body: {
          name: input.name,
          sourceName,
          documentLevelType: input.documentLevelType,
          notes: input.notes || null,
          targetEntities: Array.from(
            new Set(bindingsList.map((b) => b.entity)),
          ),
          bindings: bindingsList,
          promptText: prompt,
        },
      });
      updateSnackBar({
        severity: "success",
        message: `Template "${input.name}" saved.`,
      });
      setSaveDialogMode(null);
      if (input.alsoCreateEntities) {
        await handleMaterialize();
      } else {
        handleStartOver();
        navigate(`/studio${search}`);
      }
    } catch (error: unknown) {
      updateSnackBar({
        severity: "error",
        message: extractApiError(error, "Failed to save template."),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopyJson = () => {
    if (!session?.draftExtraction) return;
    navigator.clipboard.writeText(
      JSON.stringify(session.draftExtraction, null, 2),
    );
    updateSnackBar({ severity: "success", message: "Copied JSON." });
  };

  const busy = isUploading || isExtracting;

  let statusLabel = "Pending";
  let statusColor: "default" | "success" | "error" = "default";
  if (isExtracting) {
    statusLabel = "Analyzing…";
  } else if (session?.extractionError) {
    statusLabel = "Error";
    statusColor = "error";
  } else if (session?.draftExtraction) {
    statusLabel = "Ready";
    statusColor = "success";
  }

  if (materializeResult) {
    const linkSearchParams = new URLSearchParams(searchParams);
    linkSearchParams.delete("mode");
    return (
      <MaterializeResultView
        result={materializeResult}
        documentPath={session?.documentPath ?? null}
        searchParamsString={linkSearchParams.toString()}
        onBackToTemplates={handleResultBackToTemplates}
        onViewDocument={handleResultViewDocument}
        onRetry={handleRetryMaterialize}
      />
    );
  }

  if (!session) {
    return (
      <StudioStartPanel
        mode={mode}
        busy={busy}
        isUploading={isUploading}
        isExtracting={isExtracting}
        practiceId={practiceId}
        setPracticeId={setPracticeId}
        selectedPractices={selectedPractices}
        selectedTemplateId={selectedTemplateId}
        setSelectedTemplateId={setSelectedTemplateId}
        templates={templates}
        prompt={prompt}
        setPrompt={setPrompt}
        pendingFile={pendingFile}
        setPendingFile={setPendingFile}
        pendingBlobUrl={pendingBlobUrl}
        sampleBlobUrl={sampleBlobUrl}
        onAnalyze={handleAnalyze}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "calc(100vh - 64px)",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <StudioSessionToolbar
        mode={mode}
        session={session}
        isUploading={isUploading}
        isExtracting={isExtracting}
        appliedTemplateMode={appliedTemplateMode}
        templateDetails={templateDetails}
        statusLabel={statusLabel}
        statusColor={statusColor}
        materializing={materializing}
        saving={saving}
        sourceName={sourceName}
        missingRequiredLabels={missingRequired.map((m) => m.label)}
        saveBlocked={saveBlocked}
        onReplace={handleReplace}
        onStartOver={handleStartOver}
        onOpenSaveDialog={setSaveDialogMode}
        onMaterialize={handleMaterialize}
      />

      <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
        <Box
          sx={{
            flex: 1,
            borderRight: "1px solid #E0E0E0",
            backgroundColor: "#F5F5F5",
            position: "relative",
          }}
        >
          {pdfBlobUrl && (
            <iframe
              title="Studio PDF Preview"
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: 0 }}
            />
          )}
          {isLoadingPdf && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(245,245,245,0.92)",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={24} />
                <Typography variant="caption" color="text.secondary">
                  Loading PDF…
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
            position: "relative",
          }}
        >
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v)}
            sx={{ borderBottom: "1px solid #EEE", minHeight: 40 }}
          >
            <Tab
              value="extract"
              label="Extract"
              sx={{ minHeight: 40, textTransform: "none" }}
            />
            <Tab
              value="map"
              label={`Map${bindingCount > 0 ? ` · ${bindingCount}` : ""}`}
              disabled={!session.draftExtraction}
              sx={{ minHeight: 40, textTransform: "none" }}
            />
            <Tab
              value="preview"
              label="Preview"
              disabled={!session.draftExtraction || bindingCount === 0}
              sx={{ minHeight: 40, textTransform: "none" }}
            />
          </Tabs>

          {tab === "extract" && (
            <StudioExtractTab
              prompt={prompt}
              setPrompt={setPrompt}
              isExtracting={isExtracting}
              isUploading={isUploading}
              onReextract={handleReextract}
              draftExtraction={session.draftExtraction}
              extractionError={session.extractionError}
              onCopyJson={handleCopyJson}
            />
          )}

          {tab === "map" && (
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <MappingPanel
                entitySchema={entitySchema}
                flattenedPaths={flattenedPaths}
                iterationRules={iterationRules}
                bindings={bindings}
                pathPreviews={pathPreviews}
                sourceName={sourceName}
                supportedSources={supportedSources}
                onSourceChange={setSourceName}
                onBindingChange={handleBindingChange}
              />
            </Box>
          )}

          {tab === "preview" && (
            <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
              <EntityPreview
                tree={previewTree}
                saveDisabled={saveBlocked}
                onSave={
                  appliedTemplateMode
                    ? undefined
                    : () => setSaveDialogMode("create")
                }
              />
            </Box>
          )}

          {isExtracting && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.85)",
                pointerEvents: "none",
              }}
            >
              <Stack alignItems="center" spacing={1}>
                <CircularProgress size={32} />
                <Typography variant="body2" color="text.secondary">
                  Analyzing…
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>

      <SaveTemplateDialog
        open={saveDialogMode !== null}
        mode={saveDialogMode ?? "create"}
        sourceName={sourceName}
        bindingCount={bindingCount}
        saving={saving || materializing}
        initialName={
          saveDialogMode === "variant" && templateDetails
            ? `${templateDetails.name} (variant)`
            : ""
        }
        initialDocumentLevelType={
          saveDialogMode === "variant"
            ? (templateDetails?.documentLevelType ?? "")
            : ""
        }
        initialNotes={
          saveDialogMode === "variant" ? (templateDetails?.notes ?? "") : ""
        }
        onClose={() => setSaveDialogMode(null)}
        onSave={handleSaveTemplate}
      />
    </Box>
  );
}
